import {
  Button,
  ButtonGroup,
  Popover,
  useDisclosure,
  RadioGroup,
  Stack,
  Text,
  NativeSelect,
  Icon,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { t } from "i18next";
import {
  CraAttributes,
  CraAttributeName,
  CrashType,
  TreatmentType,
} from "@keita/lanepatrol-sdk";
import {
  MapColoring,
  MapColoringType,
} from "../../../../map/models/MapColoring";
import { tScoreType } from "../../../../coding/utils/score-utils";
import { CraAttributeUtils } from "../../../../coding/utils/attribute-utils";
import { CustomPopover } from "../../../../shared/views/CustomPopover";
import { ChevronDownIcon } from "../../../../shared/views/icons";
import { HlaAttributeName } from "../../../../map/models/HlaAttribute";
import { useStores } from "../../../../shared/stores/RootStore";
import { observer } from "mobx-react";

const crashTypes = Object.values(CrashType);
const mapColoringTypes = Object.values(MapColoringType);
const defaultScore = "global";
const defaultTreatment =
  TreatmentType.UpgradeExistingFacilityToAnOnRoadBicycleLaneWithLightSegregation;
const defaultCraAtribute = CraAttributes.all.at(0)!;
const defaultHlaAtribute = HlaAttributeName.Type;

const MapColoringSelectorBase = ({
  value,
  onChange,
}: {
  value: MapColoring;
  onChange: (coloring: MapColoring) => void;
}) => {
  const { surveyStore, filterStore } = useStores();
  const popover = useDisclosure();
  const [coloring, setColoring] = useState(value);

  useEffect(() => {
    if (popover.open) setColoring(value);
    //eslint-disable-next-line
  }, [popover.open]);

  const changeType = (type: MapColoringType) => {
    if (MapColoringType.Score === type) {
      setColoring({ type, scoreKey: defaultScore });
    } else if (MapColoringType.Treatment === type) {
      setColoring({ type, treatmentType: defaultTreatment });
    } else if (MapColoringType.CraAttribute === type) {
      setColoring({ type, attributeName: defaultCraAtribute.name });
    } else if (MapColoringType.HlaAttribute === type) {
      setColoring({ type, attributeName: defaultHlaAtribute });
    }
  };

  const submit = () => {
    onChange(coloring);
    popover.onClose();
  };

  return (
    <CustomPopover
      open={popover.open}
      trigger={
        <Button
          colorPalette="primary"
          fontSize={"sm"}
          size="sm"
          variant="plain"
          onClick={popover.onToggle}
        >
          <Text lineClamp={1} whiteSpace="pre-line">
            {MapColoringType.Score === value.type &&
              (value.scoreKey === "global"
                ? "Global score"
                : tScoreType(value.scoreKey))}
            {MapColoringType.Treatment === value.type &&
              t(`sdk:treatments.${value.treatmentType}`)}
            {MapColoringType.CraAttribute === value.type &&
              CraAttributeUtils.getLabel(value.attributeName)}
            {MapColoringType.HlaAttribute === value.type &&
              t(`shared:hlaAttribute.${value.attributeName}`, {
                speedUnit: surveyStore.speedUnitStr,
              })}
          </Text>
          <Icon as={ChevronDownIcon} />
        </Button>
      }
      onClose={popover.onClose}
      width={400}
      maxH="360px"
    >
      {popover.open && (
        <>
          <Popover.Header>Line coloring based on...</Popover.Header>
          <Popover.Body overflowY="auto">
            <NativeSelect.Root mb={2}>
              <NativeSelect.Field
                onChange={(event) =>
                  changeType(event.target.value as MapColoringType)
                }
                value={coloring.type}
              >
                {mapColoringTypes.map((type, i) => (
                  <option value={type} key={i}>
                    {t(`map:mapColoring.type.${type}`)}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            {MapColoringType.Score === coloring.type && (
              <RadioGroup.Root
                py={2}
                value={coloring.scoreKey}
                onValueChange={({ value }) => {
                  setColoring({
                    type: MapColoringType.Score,
                    scoreKey: value as CrashType,
                  });
                }}
                size="sm"
                colorPalette={"primary"}
              >
                <Stack>
                  <RadioGroup.Item key="global" value="global">
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemIndicator />
                    <RadioGroup.ItemText>Global score</RadioGroup.ItemText>
                  </RadioGroup.Item>
                  {crashTypes.map((key, i) => (
                    <RadioGroup.Item key={i} value={key}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>
                        {tScoreType(key)}
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </Stack>
              </RadioGroup.Root>
            )}

            {MapColoringType.Treatment === coloring.type && (
              <RadioGroup.Root
                py={2}
                size="sm"
                colorPalette={"primary"}
                value={coloring.treatmentType}
                onValueChange={({ value }) => {
                  setColoring({
                    type: MapColoringType.Treatment,
                    treatmentType: value as TreatmentType,
                  });
                }}
              >
                <Stack>
                  {filterStore.treatmentsAvailableInCurrentMode.map(
                    (treatment, i) => (
                      <RadioGroup.Item key={i} value={treatment}>
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemIndicator />
                        <RadioGroup.ItemText>
                          {t(`sdk:treatments.${treatment}`)}
                        </RadioGroup.ItemText>
                      </RadioGroup.Item>
                    ),
                  )}
                </Stack>
              </RadioGroup.Root>
            )}

            {MapColoringType.CraAttribute === coloring.type && (
              <RadioGroup.Root
                py={2}
                value={coloring.attributeName}
                size="sm"
                colorPalette={"primary"}
                onValueChange={({ value }) => {
                  setColoring({
                    type: MapColoringType.CraAttribute,
                    attributeName: value as CraAttributeName,
                  });
                }}
              >
                <Stack>
                  {CraAttributes.all.map((attribute, i) => (
                    <RadioGroup.Item key={i} value={`${attribute.name}`}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>
                        {CraAttributeUtils.getLabel(attribute.name)}
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </Stack>
              </RadioGroup.Root>
            )}

            {MapColoringType.HlaAttribute === coloring.type && (
              <RadioGroup.Root
                py={2}
                value={coloring.attributeName}
                size="sm"
                colorPalette={"primary"}
                onValueChange={({ value }) => {
                  setColoring({
                    type: MapColoringType.HlaAttribute,
                    attributeName: value as HlaAttributeName,
                  });
                }}
              >
                <Stack>
                  {Object.values(HlaAttributeName).map((attribute, i) => (
                    <RadioGroup.Item key={i} value={attribute}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>
                        {t(`shared:hlaAttribute.${attribute}`, {
                          speedUnit: surveyStore.speedUnitStr,
                        })}
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </Stack>
              </RadioGroup.Root>
            )}
          </Popover.Body>
          <Popover.Footer display="flex" justifyContent="flex-end">
            <ButtonGroup colorPalette={"primary"} size={"sm"}>
              <Button onClick={submit}>{t("map:filters.submitButton")}</Button>
            </ButtonGroup>
          </Popover.Footer>
        </>
      )}
    </CustomPopover>
  );
};

export const MapColoringSelector = observer(MapColoringSelectorBase);
